import { IPersonaStyles } from '@fluentui/react';
import React, { FunctionComponent } from 'react';

import AppContext from 'App/AppContext';
import { IPIcon, IPScrollablePane, IPSpinner, IPText } from 'baseComponents';
import { IPLink } from 'baseComponents/Link/IPLink';
import { IPList } from 'baseComponents/List/IPList';
import { IPPersona, IPPersonaSize } from 'baseComponents/Persona/IPPersona';
import { IPScrollbarVisibility } from 'baseComponents/ScrollablePane/IPScrollablePane';
import { IPSpinnerSize } from 'baseComponents/Spinner/IPSpinner';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { globalStackItemStylesPaddingSceneScroll, globalStackTokensGapSmall } from 'globalStyles';
import { AuditCriteriaReviewSummary, getAuditCriteriaReviewReasonText } from 'models/auditCriteria';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { apiGetAuditCriteriaReviewSummary } from 'services/Api/auditCriteriaService';
import { apiRequest } from 'services/Auth/authConfig';
import { IWidgetRendererProps } from '../WidgetRenderer';

interface IWidgetAuditCriteriaToReviewProps extends IWidgetRendererProps {}

// the Frameworks tab of the standards administration, where the Bulk review walks the flagged items
const auditCriteriaTabUrl = '/admin/standards/auditcriteria';
const maxItems = 10;

// The requirements and controls whose audit criteria a source change flagged for re-evaluation, or the
// AI re-evaluated and changed without a review since (reason Reevaluated): the total, the first few by
// name with the reason, and the way into the Bulk review that clears them.
const WidgetAuditCriteriaToReview: FunctionComponent<IWidgetAuditCriteriaToReviewProps> = (
  props: IWidgetAuditCriteriaToReviewProps,
) => {
  const { t } = useTranslation(['widgets', 'criteria']);
  const appContext = React.useContext(AppContext);
  const history = useHistory();
  const [summary, setSummary] = React.useState<AuditCriteriaReviewSummary | undefined>(undefined);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);

  React.useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.widget, props.sharedData]);

  const loadData = async () => {
    try {
      if (isLoading) return;
      setIsLoading(true);

      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const result = await apiGetAuditCriteriaReviewSummary(maxItems, accessToken);

      setSummary(result);
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const personaStyle: Partial<IPersonaStyles> = {
    root: {
      marginBottom: 5,
      selectors: {
        '&:hover': {
          cursor: 'pointer',
        },
      },
    },
  };

  if (isLoading) {
    return (
      <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
        <IPSpinner size={IPSpinnerSize.large} />
      </IPStack>
    );
  }

  const count = summary?.count ?? 0;

  return (
    <IPStack verticalFill styles={{ root: { paddingTop: 20 } }} tokens={globalStackTokensGapSmall}>
      {count > 0 && (
        <IPStack.Item>
          <IPText>{t('widgets:AuditCriteriaToReview.Count', { count })}</IPText>
        </IPStack.Item>
      )}
      <IPStack.Item grow styles={globalStackItemStylesPaddingSceneScroll}>
        <IPScrollablePane scrollbarVisibility={IPScrollbarVisibility.auto}>
          <IPList
            items={summary?.items}
            onRenderCell={(item) => {
              if (!item) return null;

              return (
                <IPPersona
                  styles={personaStyle}
                  size={IPPersonaSize.size40}
                  text={[item.code, item.name].filter(Boolean).join(' ')}
                  secondaryText={getAuditCriteriaReviewReasonText(item.reviewRequiredReason, t)}
                  onRenderInitials={() => <IPIcon iconName="CheckList" />}
                  onClick={() => history.push(auditCriteriaTabUrl)}
                />
              );
            }}
          />
        </IPScrollablePane>
      </IPStack.Item>
      {summary !== undefined && count === 0 && (
        <IPStack.Item>
          <IPText>{t('widgets:AuditCriteriaToReview.Empty')}</IPText>
        </IPStack.Item>
      )}
      {count > 0 && (
        <IPStack.Item>
          <IPLink onClick={() => history.push(auditCriteriaTabUrl)}>{t('widgets:AuditCriteriaToReview.Open')}</IPLink>
        </IPStack.Item>
      )}
    </IPStack>
  );
};

export default WidgetAuditCriteriaToReview;
